import { getUserFromUserId } from 'src/models/Api.model';

import type { OrderWithExam } from 'src/hooks/useGetFilterOrder';

import { captureError } from 'src/utils/errorReporting';

type OrderPracticeUser = { orderId: string; practiceUserId: string };

const collectOrderPracticeUsers = (
  ordersWithExams: OrderWithExam[],
): OrderPracticeUser[] =>
  ordersWithExams.flatMap(({ id: orderId, exam }) => {
    const practiceUserId = exam?.data?.PracticeUserId?.value;
    return orderId && practiceUserId ? [{ orderId, practiceUserId }] : [];
  });

const fetchEmailByPracticeUserId = async (practiceUserIds: string[]) => {
  const uniqueIds = [...new Set(practiceUserIds)];

  const practiceUserIdEmailPairs = await Promise.all(
    uniqueIds.map(async (practiceUserId) => {
      try {
        const user = await getUserFromUserId(practiceUserId);
        return { practiceUserId, email: user?.emailAddress };
      } catch (error) {
        captureError(
          error,
          `Failed to fetch user email for ID: ${practiceUserId}`,
        );
        return { practiceUserId, email: undefined };
      }
    }),
  );

  const practiceUserIdEmailMap = new Map<string, string>();
  practiceUserIdEmailPairs.forEach(({ practiceUserId, email }) => {
    if (email) practiceUserIdEmailMap.set(practiceUserId, email);
  });

  return practiceUserIdEmailMap;
};

export const getPracticeUserEmailsByOrderId = async (
  ordersWithExams: OrderWithExam[] | undefined,
): Promise<Record<string, string>> => {
  const orderPracticeUsers = collectOrderPracticeUsers(ordersWithExams ?? []);
  if (!orderPracticeUsers.length) return {};

  const emailByPracticeUserId = await fetchEmailByPracticeUserId(
    orderPracticeUsers.map((order) => order.practiceUserId),
  );

  const emailByOrderId: Record<string, string> = {};
  orderPracticeUsers.forEach(({ orderId, practiceUserId }) => {
    const email = emailByPracticeUserId.get(practiceUserId);
    if (email) emailByOrderId[orderId] = email;
  });

  return emailByOrderId;
};
